import { useState } from 'react';
import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  Select,
  Text,
  TextField,
} from '@radix-ui/themes';
import api from '../../utils/api';

// ────────────────────────────────────────────
// AI question drafts: types shared with the editor
// ────────────────────────────────────────────

// AI credits: 1 credit = 1 drafted question, reset each calendar month.
// credits and remaining are null when the org has no monthly cap.
export interface AiStatus {
  enabled: boolean;
  creditsUsed?: number;
  credits?: number | null;
  remaining?: number | null;
  maxPerRequest?: number;
}

export function creditsLeftText(status: AiStatus): string | null {
  if (status.credits == null || status.remaining == null) return null;
  return `${status.remaining} of ${status.credits} credits left this month`;
}

// One prior play of a question like this one, from the variety check.
export interface RepeatMatch {
  venueName: string;
  playedAt: string;
  similarity: number;
  text: string;
  answer?: string;
  quizName?: string;
}

export type RepeatMap = Record<string, RepeatMatch[]>;

export interface AiDraftResult<Q> {
  questions: Q[];
  repeats: RepeatMap;
  repeatCheckFailed?: boolean;
  usage: AiStatus;
}

type Difficulty = 'easy' | 'medium' | 'hard';

// ────────────────────────────────────────────
// Dialog: the host describes the questions, the server writes drafts
// ────────────────────────────────────────────

export default function AiDraftDialog<Q>({
  open,
  onOpenChange,
  roundId,
  roundTitle,
  status,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roundId: string;
  roundTitle: string;
  status: AiStatus;
  onCreated: (result: AiDraftResult<Q>) => void;
}) {
  const maxCount = status.maxPerRequest ?? 20;
  const [count, setCount] = useState('8');
  const [topic, setTopic] = useState(roundTitle);
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [style, setStyle] = useState('one-line answers');
  const [avoidExisting, setAvoidExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = parseInt(count, 10);
  const countValid = Number.isInteger(n) && n >= 1 && n <= maxCount;
  const remaining = status.remaining;
  const outOfCredits = remaining != null && remaining <= 0;
  // 1 credit per question: block a request for more than the credits left.
  const overCredits = countValid && remaining != null && n > remaining;
  const canSubmit = countValid && topic.trim().length > 0 && !busy && !outOfCredits && !overCredits;
  const creditsText = creditsLeftText(status);

  async function handleSubmit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/ai-drafts/round/${roundId}`, {
        count: n,
        topic: topic.trim(),
        difficulty,
        style: style.trim(),
        avoidExisting,
      });
      onCreated(res.data.data as AiDraftResult<Q>);
      onOpenChange(false);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message || e.message || 'The drafts could not be made');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>Draft questions with AI</Dialog.Title>
        <Dialog.Description size="2" mb="3" color="gray">
          The drafts go to the end of this round. A draft does not play at a
          game night until you approve it.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <Flex gap="3">
            <label style={{ flex: '0 0 96px' }}>
              <Text as="div" size="2" mb="1" weight="medium">Questions</Text>
              <TextField.Root
                type="number"
                min={1}
                max={maxCount}
                value={count}
                onChange={(e) => setCount(e.target.value)}
              />
            </label>
            <label style={{ flex: 1 }}>
              <Text as="div" size="2" mb="1" weight="medium">Difficulty</Text>
              <Select.Root value={difficulty} onValueChange={(v) => setDifficulty(v as Difficulty)}>
                <Select.Trigger style={{ width: '100%' }} aria-label="Difficulty" />
                <Select.Content>
                  <Select.Item value="easy">Easy</Select.Item>
                  <Select.Item value="medium">Medium</Select.Item>
                  <Select.Item value="hard">Hard</Select.Item>
                </Select.Content>
              </Select.Root>
            </label>
          </Flex>

          <label>
            <Text as="div" size="2" mb="1" weight="medium">Topic</Text>
            <TextField.Root
              value={topic}
              maxLength={200}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="90s movies"
            />
          </label>

          <label>
            <Text as="div" size="2" mb="1" weight="medium">Style</Text>
            <TextField.Root
              value={style}
              maxLength={200}
              onChange={(e) => setStyle(e.target.value)}
              placeholder="one-line answers, no multiple choice"
            />
          </label>

          <Text as="label" size="2">
            <Flex gap="2" align="center">
              <Checkbox
                checked={avoidExisting}
                onCheckedChange={(v) => setAvoidExisting(v === true)}
              />
              Do not repeat the questions already in this round
            </Flex>
          </Text>

          {!countValid && (
            <Text size="1" color="red">Ask for 1 to {maxCount} questions.</Text>
          )}
          {creditsText && (
            <Text size="1" color="gray">
              {creditsText}. Each question uses 1 credit.
            </Text>
          )}
          {outOfCredits && (
            <Callout.Root color="amber" size="1">
              <Callout.Text>
                Your organization used all AI credits for this month. Credits reset on the
                first day of next month.
              </Callout.Text>
            </Callout.Root>
          )}
          {!outOfCredits && overCredits && (
            <Callout.Root color="amber" size="1">
              <Callout.Text>
                You asked for {n} questions, but only {remaining} {remaining === 1 ? 'credit is' : 'credits are'} left
                this month. Ask for {remaining} or fewer.
              </Callout.Text>
            </Callout.Root>
          )}
          {error && (
            <Callout.Root color="red" size="1">
              <Callout.Text>{error}</Callout.Text>
            </Callout.Root>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={busy}>Cancel</Button>
          </Dialog.Close>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={busy}>
            Write drafts
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
